import * as R from 'ramda';
import React from 'react';
import { branch, renderComponent, renderNothing, mapProps, compose } from 'recompose';
import Spinner from 'components/general/Spinner/Spinner';
import ErrorMessage from 'components/general/ErrorMessage/ErrorMessage';

/**
 * Displays loading/error states based on 'loading' object.
 * @param {Object} [options]
 * @param {String} options.itemsProp=items The prop the Component will be
 * passed that relates to the 'items'/content that is loaded.
 * @param {Function} options.renderLoading=Spinner
 * A render-function that will be used during the 'loading' state. Good place
 * for skeletons.
 * @returns {HOC}
 */
const withLoadingError = ({
  itemsProp = 'items',
  renderLoading = () => <Spinner />,
}: options) => compose(
  branch(
    R.pipe(
      R.path(['loading', 'state']),
      R.equals('NOT_LOADED'),
    ),
    renderNothing,
  ),
  branch(
    R.pipe(
      R.path(['loading', 'state']),
      R.equals('LOADING'),
    ),
    renderComponent(renderLoading),
  ),
  branch(
    R.pipe(
      R.path(['loading', 'errors']),
      R.both(
        R.complement(R.isEmpty),
        R.complement(R.isNil),
      ),
    ),
    renderComponent(props => (
      <ErrorMessage
        icon="activity"
        title="Could not load content."
        retry={props.retry}
      />
    )),
  ),
  branch(
    R.pipe(
      R.prop(itemsProp),
      R.isEmpty,
    ),
    renderComponent(() => (
      <ErrorMessage
        title="No results found."
        icon="search"
        types={['neutral']}
        message="Try un-setting some filters."
      />
    )),
  ),
  mapProps(R.omit(['loading'])),
);

interface options {
  itemsProp?: string;
  renderLoading?();
}

export default withLoadingError;

